import { useEffect, useRef, useState } from 'react';
import { Box, Button, Stack, StackProps, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { OtpData } from 'api/types';
import { Controller, useFormContext } from 'react-hook-form';
import { UseMutationResult } from '@tanstack/react-query';
import { useServerErrors } from '../../../hooks';
import { OtpFlow } from '../OtpFlow';

export type OptFieldProps = StackProps & {
  method?: string; // SMS or ANY
  name?: string;
  nameOtpId?: string;
  sendFields: string[];
  mutation: UseMutationResult<any, any, any, any>;
};

export const OptField: React.FC<OptFieldProps> = ({
  method = 'ANY',
  name = 'code',
  nameOtpId = 'otpId',
  sendFields = [],
  mutation,
  ...stackProps
}) => {
  const { t } = useTranslation();
  const codeInputRef = useRef<HTMLInputElement>(null);

  const { addServerErrorActions, getServerErrorStatus } = useServerErrors();
  const [otpData, setOtpData] = useState<OtpData | null>(null);
  const {
    getValues,
    setValue,
    setError,
    resetField,
    formState: { isSubmitting, isSubmitSuccessful },
  } = useFormContext();

  const { mutateAsync: getOtpData, isLoading: isGettingOtpData } = mutation;

  /**
   * Clear fields after submitted form
   * OPT code can be used only one time
   */
  useEffect(() => {
    if (isSubmitSuccessful && !isSubmitting) {
      resetField(name);
      resetField(nameOtpId);
      setOtpData(null);
    }
  }, [isSubmitSuccessful, isSubmitting, name, nameOtpId, resetField]);

  /**
   * Reset fields on mount
   */
  useEffect(() => {
    setValue(name, undefined);
    setValue(nameOtpId, undefined);
  }, [name, nameOtpId, setValue]);

  useEffect(() => {
    if (!getValues(name)) {
      setError(name, {
        type: 'required',
        message: t('common:formErrors.required'),
      });
    }
  }, [getValues, name, setError, t]);

  useEffect(() => {
    if (otpData) {
      codeInputRef.current?.focus();
      setValue(nameOtpId, otpData.id);
    } else {
      setValue(nameOtpId, undefined);
    }
  }, [nameOtpId, otpData, setValue]);

  const handleServerErrors = (error: unknown) => {
    const errors = getServerErrorStatus(error);
    Object.entries(errors).forEach(([field, message]) => {
      setError(field, {
        type: 'server',
        message,
      });
    });
    addServerErrorActions(error);
  };

  const handleGetOtpCode = async () => {
    resetField(name);
    resetField(nameOtpId);
    setError(name, {});

    const values = sendFields.reduce(
      (acc, field) => ({
        ...acc,
        [field]: getValues(field),
      }),
      {},
    );

    try {
      const data = await getOtpData(values as any);
      setOtpData(data);
    } catch (error) {
      handleServerErrors(error);
    }
  };

  return (
    <Stack
      p={24}
      sx={theme => ({
        background: theme.colors.green[1],
      })}
      {...stackProps}
    >
      <Text fz="lg" fw={600}>
        {t('forms:external.otpTitle')}
      </Text>
      <Text fz="lg" fw={500}>
        {t(
          method === 'SMS'
            ? 'forms:external.otpDescriptionPhone'
            : 'forms:external.otpDescriptionEmail',
        )}
      </Text>

      {!otpData && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            color="green"
            onClick={handleGetOtpCode}
            loading={isGettingOtpData}
          >
            {t('forms:external.otpSendCode')}
          </Button>
        </Box>
      )}

      {otpData && (
        <Controller
          name={name}
          rules={{ required: true, minLength: 6, maxLength: 6 }}
          render={({ field, fieldState: { error } }) => (
            <OtpFlow
              error={error?.message}
              codeNumber={otpData.order}
              validUntil={otpData.validUntil}
              method={otpData?.method}
              isGettingOtpData={isGettingOtpData}
              resendCode={handleGetOtpCode}
              textInputProps={field as any}
            />
          )}
        />
      )}
    </Stack>
  );
};
